import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api.js'
import { Badge, Card, CopyButton, Field, Notice, RowsSkeleton } from './ui.js'

type Method = 'totp' | 'whatsapp'

interface Status {
  method: Method | null
  phoneHint: string | null
}

const LABEL: Record<Method, string> = { totp: 'Authenticator app', whatsapp: 'WhatsApp code' }

/**
 * Turning two-step sign-in on and off. Used by the merchant Security tab and
 * the Super Admin profile. Each change is proven with a code before it saves,
 * so a typo can never lock an account out.
 */
export function TwoFactorCard(): React.ReactNode {
  const queryClient = useQueryClient()
  const status = useQuery({ queryKey: ['2fa'], queryFn: () => api<Status>('/v1/auth/2fa') })
  const done = (): void => {
    setStep(null)
    setCode('')
    void queryClient.invalidateQueries({ queryKey: ['2fa'] })
  }

  /** What is being proven right now: a new authenticator, WhatsApp, or turning it off. */
  const [step, setStep] = useState<null | 'totp' | 'whatsapp' | 'disable'>(null)
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null)
  const [code, setCode] = useState('')

  const begin = useMutation({
    mutationFn: async (next: 'totp' | 'whatsapp' | 'disable') => {
      if (next === 'totp') setSetup(await api<{ secret: string; uri: string }>('/v1/auth/2fa/totp/setup', { method: 'POST' }))
      // Off-switch for a WhatsApp account needs a fresh code; an authenticator already has one.
      if (next === 'whatsapp' || (next === 'disable' && status.data?.method === 'whatsapp')) {
        await api('/v1/auth/2fa/whatsapp/send', { method: 'POST' })
      }
      return next
    },
    onSuccess: (next) => {
      setCode('')
      setStep(next)
    },
  })

  const confirm = useMutation({
    mutationFn: () => {
      const path = step === 'totp' ? '/v1/auth/2fa/totp/enable' : step === 'whatsapp' ? '/v1/auth/2fa/whatsapp/enable' : '/v1/auth/2fa/disable'
      return api(path, { method: 'POST', body: { code } })
    },
    onSuccess: done,
  })

  const method = status.data?.method ?? null
  const error = (begin.error ?? confirm.error) as Error | null

  return (
    <Card title="Two-step verification" subtitle="A code on top of your password or phone sign-in.">
      {status.isPending && <RowsSkeleton rows={2} />}
      {status.error && <Notice kind="error">{(status.error as Error).message}</Notice>}

      {status.data && !step && (
        <div className="stack">
          <p>
            {method ? (
              <Badge tone="ok" dot>
                On — {LABEL[method]}
              </Badge>
            ) : (
              <Badge tone="warn">Off</Badge>
            )}
          </p>

          {method ? (
            <button type="button" className="btn btn--danger btn--sm" disabled={begin.isPending} onClick={() => begin.mutate('disable')}>
              Turn off
            </button>
          ) : (
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn btn--primary btn--sm" disabled={begin.isPending} onClick={() => begin.mutate('totp')}>
                Use an authenticator app
              </button>
              <button
                type="button"
                className="btn btn--sm"
                disabled={begin.isPending || !status.data.phoneHint}
                title={status.data.phoneHint ? undefined : 'This account has no phone number.'}
                onClick={() => begin.mutate('whatsapp')}
              >
                Use WhatsApp codes
              </button>
            </div>
          )}
        </div>
      )}

      {step && (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            confirm.mutate()
          }}
        >
          {step === 'totp' && setup && (
            <>
              <p className="muted" style={{ fontSize: '.87rem' }}>
                Add this account to Google Authenticator, Microsoft Authenticator or any TOTP app. On this phone,{' '}
                <a href={setup.uri}>open it in your app</a>; otherwise type the setup key in.
              </p>
              <Field label="Setup key">
                <div className="row" style={{ gap: 8 }}>
                  <code style={{ wordBreak: 'break-all' }}>{setup.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
                  <CopyButton value={setup.secret} />
                </div>
              </Field>
            </>
          )}

          {step !== 'totp' && (step === 'whatsapp' || method === 'whatsapp') && (
            <p className="muted" style={{ fontSize: '.87rem' }}>
              We sent a code on WhatsApp to the number ending {status.data?.phoneHint ?? '••••'}.
            </p>
          )}

          {step === 'disable' && method === 'totp' && (
            <p className="muted" style={{ fontSize: '.87rem' }}>
              Enter the current code from your authenticator app.
            </p>
          )}

          <Field label="Code">
            <input
              className="input"
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={8}
              autoFocus
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
          </Field>

          <div className="row" style={{ gap: 8 }}>
            <button type="submit" className={step === 'disable' ? 'btn btn--danger' : 'btn btn--primary'} disabled={code.length < 4 || confirm.isPending}>
              {confirm.isPending ? 'Checking…' : step === 'disable' ? 'Turn off' : 'Turn on'}
            </button>
            <button type="button" className="btn" onClick={() => setStep(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {error && <Notice kind="error">{error.message}</Notice>}
    </Card>
  )
}
